import { useMemo } from 'react';

import { QuotedColor } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { RadioCard } from '@ravago/shared/radiance/components/radio/components/RadioCard/RadioCard';
import { RadioCardGroup } from '@ravago/shared/radiance/components/radio/components/RadioCardGroup/RadioCardGroup';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { hasMtsLocalLeadTime, LeadTimeCheckCriteria } from '../../../../../utils/lead-time';
import { LeadTimeMessage } from '../../../../shared/lead-time-message/lead-time-message';

interface Props {
   quotedColor: QuotedColor;
   selectedLineId: string | undefined;
   leadTimeIndicators: LeadTimeIndicator[];
   leadTimeEnabled: boolean;
   onSelectedLineChange: (selectedLineId: string) => void;
}

export const DynamicProductBlockQuotedColorFields = ({
   quotedColor,
   selectedLineId,
   leadTimeIndicators,
   leadTimeEnabled,
   onSelectedLineChange
}: Props) => {
   const translate = useTranslations();
   const format = useFormatter();

   const quotationLines = useMemo(() => quotedColor.quotation.lines.sort((a, b) => a.quantity.amount - b.quantity.amount) ?? [], [quotedColor]);

   const selectedLine = quotationLines.find((quotationLine) => quotationLine.id === selectedLineId);

   const bestLineId = useMemo(() => {
      const hasMultipleLines = quotationLines.length > 1;
      if (!hasMultipleLines) return undefined;

      const firstLinePrice = quotationLines[0].unitPrice.price.amount;
      const allSamePrice = quotationLines.every((line) => line.unitPrice.price.amount === firstLinePrice);
      if (allSamePrice) return undefined;

      return quotationLines.find((line) => line.bestPrice)?.id;
   }, [quotationLines]);

   const totalPrice = useMemo(() => {
      if (!selectedLine) return 0;

      const updatedLine = quotationLines.find((line) => line.id === selectedLine.id);
      return (updatedLine?.unitPrice.price.amount ?? 0) * (updatedLine?.quantity.amount ?? 0);
   }, [selectedLine, quotationLines]);

   const hasMtsLocal = useMemo(() => {
      if (!selectedLine || !leadTimeEnabled || leadTimeIndicators.length === 0) {
         return false;
      }
      const hasLine = quotedColor.quotation.lines.some((line) => line.id === selectedLine.id);
      if (!hasLine || !quotedColor.quotation.deliveryInformation?.address) {
         return false;
      }
      if (!quotedColor.quotation || !quotedColor.quotation.deliveryInformation?.address) {
         return false;
      }

      const criteria: LeadTimeCheckCriteria = {
         colorId: quotedColor.color.id,
         producerColorCode: quotedColor.color.producerColorCode,
         packagingGroupId: selectedLine.packagingGroupId,
         addressId: quotedColor.quotation.deliveryInformation.address.id
      };

      return hasMtsLocalLeadTime(leadTimeIndicators, criteria);
   }, [selectedLine, leadTimeEnabled, leadTimeIndicators, quotedColor]);

   return (
      <Box items="center" justify="between">
         <Box direction="column" width="100%" gap="md">
            <Typography variant="body-highlight" id="select-quantity-label">
               {translate('dynamic-product-block.select-quantity')}
            </Typography>
            <RadioCardGroup ariaLabelledBy="select-quantity-label" value={selectedLineId ?? ''} onChange={onSelectedLineChange}>
               {quotationLines.map((line) => (
                  <RadioCard key={line.id} value={line.id}>
                     <Box direction="column" gap="sm">
                        <Box gap="sm" justify="between">
                           <Typography variant="body-highlight">{format.quantity(line.quantity)}</Typography>
                           <Box gap="sm">
                              {bestLineId === line.id && <Badge variant="success">{translate('dynamic-product-block.best-price')}</Badge>}
                              <Typography>
                                 {format.unitPrice(line.unitPrice)}
                                 {line.isPriceFixed === false && '*'}
                              </Typography>
                           </Box>
                        </Box>
                        <Box direction="column" gap="2xs">
                           <Box gap="2xs" items="center">
                              <Icon icon="box-alt" color="subtle" />
                              <Typography color="subtle"> {line.packaging}</Typography>
                           </Box>
                           {line.isPriceFixed === false && (
                              <Typography variant="small" color="subtle">
                                 *
                                 {line.marketPricingMonth
                                    ? translate('commodity-pricing.price-contingent-to-month-index', {
                                         month: format.fullMonthName(line.marketPricingMonth)
                                      })
                                    : translate('commodity-pricing.to-be-confirmed')}
                              </Typography>
                           )}

                           {line.remark && (
                              <Box gap="2xs" items="center">
                                 <Icon icon="comment" color="subtle" />
                                 <Typography color="subtle">{line.remark}</Typography>
                              </Box>
                           )}
                        </Box>
                     </Box>
                  </RadioCard>
               ))}
            </RadioCardGroup>
            <Divider />
            {selectedLine && (
               <Box justify="between">
                  <Typography>{translate('dynamic-product-block.total-price')}</Typography>
                  <Box direction="column" gap="2xs" items="end">
                     <Typography variant="body-highlight">
                        {totalPrice
                           ? format.price({
                                amount: totalPrice,
                                currency: selectedLine.unitPrice.price.currency
                             })
                           : '-'}
                        {selectedLine.isPriceFixed === false && '*'}
                     </Typography>
                     {selectedLine.isPriceFixed === false && (
                        <Typography variant="small" color="subtle">
                           *{translate('commodity-pricing.to-be-confirmed')}
                        </Typography>
                     )}
                  </Box>
               </Box>
            )}
            {leadTimeEnabled && <LeadTimeMessage hasMtsLocal={hasMtsLocal} from="order-now" />}
         </Box>
      </Box>
   );
};
